/**
 * Venta al Contado — migración web de ares2015 (escritorio).
 *
 * El cliente paga el precio completo: NO se crea préstamo ni amortización.
 * Reutiliza los endpoints de la venta financiada para cargar configuración,
 * buscar el cliente por cédula y validar el chasis; guarda con
 * /api/ventacontado_guardar (factmot con financia=precio y noprest=0, solicit
 * con noprest=0, producto vendido). Si el precio es menor al costo, solicita
 * autorización por la tabla de autorizaciones. Imprime el recibo de contado.
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Avatar from "@mui/material/Avatar";
import InputAdornment from "@mui/material/InputAdornment";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import AddAPhotoOutlinedIcon from "@mui/icons-material/AddAPhotoOutlined";
import GppMaybeOutlinedIcon from "@mui/icons-material/GppMaybeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, DocumentoField, fpInputSx } from "components/financepro";
import { useApi } from "lib/api";
import { imprimirReciboPdf } from "lib/recibopdf";
import { imprimirFacturaMotor } from "modules/facturacion/impresionfacturamotor";
import { documentoValido, etiquetaDocumento } from "lib/documento";
import AlertaRed from "modules/dataorbit/alertaventa";
import { useBorradorVenta } from "lib/borradorventa";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AvisoBorradorVenta from "components/borradorventa/AvisoBorradorVenta";

const FORM_INICIAL = {
  // Datos generales
  cedula: "", nombres: "", apellidos: "", direccion: "", provincia: "",
  telefono: "", celular: "", email: "", sexo: "Femenino", ecivil: "Soltero",
  depend: "0", nacionalidad: "Dominicano", edad: "", apodo: "",
  // Venta
  comentario: "", chasis: "", marca: "", modelo: "", colore: "",
  precio: "", tipopago: "Contado", descripcionpago: "",
  // Formas combinadas: vacío = una sola forma (tipopago/descripcionpago por
  // el precio completo); con 2+ cada una lleva su monto y deben sumar el precio.
  pagos: [],
  // Vendedor (usuario del sistema): solo con usavendedor = S en configuración.
  vendedor: "",
};

// Filtro local del chasis: matchea por chasis, marca, modelo o color.
const filtrarChasis = createFilterOptions({
  stringify: (o) => (typeof o === "string" ? o : `${o.chasis} ${o.marca || ""} ${o.modelo || ""} ${o.colore || ""}`),
  limit: 60,
});

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const toNum = (v) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

// Reduce la foto a 80x80 JPEG y la devuelve en base64 (igual que el escritorio).
function comprimirFoto(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 80;
      canvas.height = 80;
      canvas.getContext("2d").drawImage(img, 0, 0, 80, 80);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", 0.7).split(",")[1] || "");
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Foto inválida"));
    };
    img.src = url;
  });
}

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box sx={{ width: 32, height: 32, borderRadius: 1.4, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: `${color}14`, color }}>
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography sx={{ fontWeight: 800, fontSize: "0.74rem", letterSpacing: "0.1em", textTransform: "uppercase", color: FP.color.textSecondary }}>
        {children}
      </Typography>
    </Box>
  );
}

// Recibo de venta al contado, en PDF con el formato de bankivo (lib/recibopdf).
// Con mostraritbis activo se agrega la línea de ITBIS calculado encima del
// precio (precio × tasa%), pero es informativa: el Total es el propio precio
// de venta (mismo criterio que el conduce, impresionfacturamotor.js).
function imprimirRecibo(recibo, compania, itbisCfg) {
  const now = new Date();
  const precio = toNum(recibo.precio);
  const tasa = toNum(itbisCfg?.tasaitbis);
  const conItbis = itbisCfg?.mostraritbis === "S" && tasa > 0 && precio > 0;
  const montoItbis = conItbis ? (precio * tasa) / 100 : 0;
  const formas = Array.isArray(recibo.formas) && recibo.formas.length > 1 ? recibo.formas : null;
  imprimirReciboPdf({
    titulo: "Recibo de Venta al Contado",
    compania,
    campos: [
      ["Fecha", `${now.toLocaleDateString("es-DO")} ${now.toLocaleTimeString("es-DO")}`],
      ["Cliente", `${recibo.nombres} ${recibo.apellidos}`],
      [etiquetaDocumento(recibo.cedula), recibo.cedula],
      ["Chasis", recibo.chasis],
      ["Vehiculo", [recibo.marca, recibo.modelo, recibo.colore].filter(Boolean).join(" ")],
      ["Tipo de pago", recibo.tipopago],
      !formas && recibo.descripcionpago ? ["Referencia", recibo.descripcionpago] : null,
    ],
    totales: [
      // Venta combinada: una línea por forma, con su referencia.
      ...(formas || []).map((f) => ({
        etiqueta: f.referencia ? `${f.tipopago} (${f.referencia})` : f.tipopago,
        valor: toNum(f.valor),
      })),
      { etiqueta: "Precio de venta", valor: precio },
      ...(conItbis ? [{ etiqueta: `ITBIS (${tasa}%)`, valor: montoItbis }] : []),
      { etiqueta: "Total", valor: precio, destacado: true },
    ],
    firma: "Firma",
  });
}

export default function VentaContado({
  embedded = false,
  onExit,
  onSaved,
  restaurarBorrador = false,
} = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [form, setForm] = useState(FORM_INICIAL);
  const setF = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  // La venta al contado no captura los campos adicionales configurables
  // (Configuración → Campos Adicionales): esos son para crédito/solicitud.
  // Tipo de documento del cliente (cedula/rnc/pasaporte): solo controla la
  // máscara y validación; en la base va en la misma columna cedula.
  const [tipodoc, setTipodoc] = useState("cedula");

  // Configuración del tenant (provincias, tipos de pago, compañía).
  const [config, setConfig] = useState(null);
  useEffect(() => {
    let activo = true;
    post("/api/ventaproducto_configuracion", {}).then(({ ok, data }) => {
      if (!activo) return;
      if (ok) setConfig(data);
      else showAlert({ message: data.Error || "No se pudo cargar la configuración", color: "error" });
    });
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const provincias = config?.provincias || [];
  // La venta al contado acepta tarjeta (la venta a crédito comparte esta
  // configuración pero usa `tipospago`, sin tarjeta).
  const tipospago = config?.tipospagocontado || config?.tipospago || ["Contado", "Transferencias"];
  const usaVendedor = config?.usavendedor === "S";

  // ---- Formas de pago combinadas ----
  const combinado = form.pagos.length > 1;
  const faltaAsignar = combinado
    ? toNum(form.precio) - form.pagos.reduce((s, x) => s + toNum(x.valor), 0)
    : 0;
  const setPago = (i, k, v) =>
    setForm((p) => ({ ...p, pagos: p.pagos.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  // Al combinar, la forma elegida arriba toma el precio completo y la nueva
  // queda en blanco para repartir; las siguientes toman lo que falte.
  const agregarPago = () =>
    setForm((p) => {
      const pagos = p.pagos.length
        ? p.pagos
        : [{ tipopago: p.tipopago, valor: p.precio, referencia: p.descripcionpago }];
      const falta = toNum(p.precio) - pagos.reduce((s, x) => s + toNum(x.valor), 0);
      const usados = new Set(pagos.map((x) => x.tipopago));
      const tipo = tipospago.find((t) => !usados.has(t)) || tipospago[0];
      return {
        ...p,
        pagos: [...pagos, { tipopago: tipo, valor: falta > 0 ? falta.toFixed(2) : "", referencia: "" }],
      };
    });
  // Quitar hasta dejar una sola forma vuelve al modo simple con esa forma.
  const quitarPago = (i) =>
    setForm((p) => {
      const pagos = p.pagos.filter((_, j) => j !== i);
      if (pagos.length > 1) return { ...p, pagos };
      const [u] = pagos;
      return { ...p, pagos: [], tipopago: u?.tipopago || p.tipopago, descripcionpago: u?.referencia || "" };
    });
  let textoAsignado = "Las formas de pago cubren el precio";
  if (faltaAsignar > 0.009) textoAsignado = `Falta asignar RD$ ${fmtMoney(faltaAsignar)}`;
  if (faltaAsignar < -0.009) textoAsignado = `Se pasa por RD$ ${fmtMoney(-faltaAsignar)}`;

  // Usuarios del sistema para el selector de vendedor (mismo endpoint que
  // la solicitud de crédito). Solo se cargan si el tenant usa vendedor.
  const [usuarios, setUsuarios] = useState([]);
  useEffect(() => {
    if (!usaVendedor) return undefined;
    let activo = true;
    post("/api/solicitudcredito_usuarios", {}).then(({ ok, data }) => {
      if (activo && ok && Array.isArray(data.usuarios)) setUsuarios(data.usuarios);
    });
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usaVendedor]);

  // ---- Cliente ----
  const [foto, setFoto] = useState("");
  const fotoInputRef = useRef(null);

  const seleccionarFoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      setFoto(await comprimirFoto(file));
    } catch {
      showAlert({ message: "Existe un error en la foto. Revise", color: "error" });
    }
  };

  const buscarPorCedula = useCallback(async () => {
    const ced = form.cedula.trim();
    if (!documentoValido(ced, tipodoc)) return;
    const { ok, data } = await post("/api/ventaproducto_buscarcliente", { cedula: ced });
    if (!ok) return;
    setFoto(data.foto || "");
    // Vendedor de la solicitud vigente: se prellena si aún no se eligió uno.
    if (data.solicitudcredito?.vendedor) {
      const vendedorSolicitud = data.solicitudcredito.vendedor;
      setForm((p) => ({ ...p, vendedor: p.vendedor || vendedorSolicitud }));
    }
    if (data.solicitud) {
      const s = data.solicitud;
      setForm((p) => ({
        ...p,
        nombres: s.nombres || p.nombres,
        apellidos: s.apellidos || p.apellidos,
        direccion: s.direccion || p.direccion,
        telefono: s.telefono || p.telefono,
        celular: s.celular || p.celular,
        provincia: s.urb || p.provincia,
        edad: s.edad != null ? String(s.edad) : p.edad,
        apodo: s.apodo || p.apodo,
        comentario: s.comentario || p.comentario,
        email: s.email || p.email,
      }));
    }
  }, [form.cedula, tipodoc, post]);

  // ---- Chasis ----
  const [chasisOpts, setChasisOpts] = useState([]);
  const [loadingChasis, setLoadingChasis] = useState(false);
  const [chasisValidado, setChasisValidado] = useState(false);

  const cargarChasis = useCallback(async () => {
    setLoadingChasis(true);
    try {
      const { ok, data } = await post("/api/ventaproducto_chasis", {});
      if (ok && Array.isArray(data.chasis)) setChasisOpts(data.chasis);
    } finally {
      setLoadingChasis(false);
    }
  }, [post]);

  useEffect(() => {
    cargarChasis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validarChasis = useCallback(
    async (chasis) => {
      if (!chasis) return;
      const { ok, data } = await post("/api/ventaproducto_validarchasis", { chasis });
      if (!ok) {
        showAlert({ message: data.Error || "No se pudo validar el chasis", color: "error" });
        setChasisValidado(false);
        setForm((p) => ({ ...p, chasis: "", marca: "", modelo: "", colore: "", precio: "" }));
        return;
      }
      if (data.inicial) {
        showAlert({ message: "Este chasis tiene un inicial registrado; no se puede vender al contado", color: "warning" });
        setChasisValidado(false);
        setForm((p) => ({ ...p, chasis: "", marca: "", modelo: "", colore: "", precio: "" }));
        return;
      }
      const precio = toNum(data.preciobase);
      setChasisValidado(true);
      setForm((p) => ({
        ...p,
        chasis: data.chasis,
        marca: data.marca,
        modelo: data.modelo,
        colore: data.colore,
        precio: precio ? String(precio) : "",
      }));
    },
    [post, showAlert]
  );

  // ---- Autorización (precio < costo) ----
  const [autOpen, setAutOpen] = useState(false);
  const [autMotivo, setAutMotivo] = useState("");
  const [autDescripcion, setAutDescripcion] = useState("");
  const [autIdtext, setAutIdtext] = useState("");
  const [autStatus, setAutStatus] = useState("");
  const [autComentario, setAutComentario] = useState("");
  const pollRef = useRef(null);

  const detenerPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };
  useEffect(() => detenerPolling, []);

  // Espera la respuesta del administrador (también al retomar un borrador
  // que quedó con la autorización en curso).
  function iniciarPolling(idtext) {
    detenerPolling();
    pollRef.current = setInterval(async () => {
      const r = await post("/api/ventaproducto_estadoautorizacion", { idtext });
      if (!r.ok) return;
      if (r.data.status === "Aprobado") {
        detenerPolling();
        setAutStatus("Aprobado");
      } else if (r.data.status === "Cancelado") {
        detenerPolling();
        setAutStatus("Cancelado");
        setAutComentario(r.data.comentario || "");
      }
    }, 5000);
  }

  const solicitarAutorizacion = async () => {
    const { ok, data } = await post("/api/ventaproducto_solicitarautorizacion", {
      descripcion: autDescripcion,
      datos: {
        cedula: form.cedula, nombre: form.nombres, apellido: form.apellidos,
        chasis: form.chasis, marca: form.marca, modelo: form.modelo,
        color: form.colore, precio: form.precio, tipo: "Contado",
      },
    });
    if (!ok) {
      showAlert({ message: data.Error || "No se pudo solicitar la autorización", color: "error" });
      return;
    }
    setAutIdtext(data.idtext);
    setAutStatus("Progreso");
    iniciarPolling(data.idtext);
  };

  const cancelarAutorizacion = async () => {
    detenerPolling();
    if (autIdtext && autStatus === "Progreso") {
      await post("/api/ventaproducto_cancelarautorizacion", { idtext: autIdtext });
    }
    setAutOpen(false);
    setAutIdtext("");
    setAutStatus("");
    setAutComentario("");
  };

  // ---- Guardar ----
  const [guardando, setGuardando] = useState(false);

  const limpiar = () => {
    detenerPolling();
    setForm(FORM_INICIAL);
    setTipodoc("cedula");
    setFoto("");
    setChasisValidado(false);
    setAutIdtext("");
    setAutStatus("");
    setAutComentario("");
  };

  const guardar = async (autorizacionid = "") => {
    if (!chasisValidado || !form.chasis) {
      showAlert({ message: "Seleccione un chasis válido de la lista", color: "warning" });
      return;
    }
    if (toNum(form.precio) <= 0) {
      showAlert({ message: "Indique el precio de la venta", color: "warning" });
      return;
    }
    if (combinado) {
      if (form.pagos.some((x) => toNum(x.valor) <= 0)) {
        showAlert({ message: "Cada forma de pago debe tener un monto mayor a 0", color: "warning" });
        return;
      }
      if (Math.abs(faltaAsignar) > 0.009) {
        showAlert({ message: `Las formas de pago deben sumar RD$ ${fmtMoney(form.precio)}`, color: "warning" });
        return;
      }
    }
    setGuardando(true);
    try {
      const { pagos, ...resto } = form;
      const { ok, status, data } = await post("/api/ventacontado_guardar", {
        ...resto,
        ...(combinado ? { pagos: pagos.map((x) => ({ ...x, valor: toNum(x.valor) })) } : {}),
        foto,
        autorizacionid,
      });
      if (ok) {
        showAlert({ message: `${data.mensajeok || "Venta registrada"} — Factura #${data.nofactura}`, color: "success" });
        // Multi-empresa: el backend devuelve el membrete de la empresa dueña del chasis.
        if (data.recibo) imprimirRecibo(data.recibo, data.compania || config?.compania, config);
        // Conduce automático de la venta (mismo documento que la reimpresión
        // del informe de motores facturados). Si falla, la venta ya quedó
        // registrada y el conduce se reimprime desde el informe.
        try {
          const det = await post("/api/facturamotor_detalle", { secuencia: data.nofactura });
          if (det.ok) imprimirFacturaMotor(det.data);
        } catch {
          /* noop */
        }
        setAutOpen(false);
        limpiar();
        cargarChasis();
        if (onSaved) onSaved();
        return;
      }
      if (status === 409 && data.requiereAutorizacion) {
        setAutMotivo(data.motivo || "Esta operación requiere autorización");
        setAutDescripcion(data.descripcion || "solicito autorizacion de venta al contado");
        setAutIdtext("");
        setAutStatus("");
        setAutComentario("");
        setAutOpen(true);
        return;
      }
      showAlert({ message: data.Error || "Error al guardar", color: "error" });
    } catch {
      showAlert({ message: "Error de red o servidor", color: "error" });
    } finally {
      setGuardando(false);
    }
  };

  // ---- Borrador ("stand by", ver lib/borradorventa) ----
  // El chasis no queda reservado mientras la venta está en pausa: al retomar
  // se vuelve a validar (sin pisar el precio digitado).
  const restaurar = async (d) => {
    detenerPolling();
    setForm({ ...FORM_INICIAL, ...(d.form || {}) });
    setTipodoc(d.tipodoc || "cedula");
    setFoto(d.foto || "");
    setChasisValidado(false);
    setAutOpen(false);
    setAutIdtext("");
    setAutStatus("");
    setAutComentario("");
    const chasis = d.form?.chasis;
    if (!chasis) return;
    const { ok, data } = await post("/api/ventaproducto_validarchasis", { chasis });
    if (!ok || data.inicial) {
      showAlert({
        message: `El chasis ${chasis} ya no está disponible para esta venta${
          ok ? " (tiene un inicial registrado)" : `: ${data.Error || "no se pudo validar"}`
        }. Seleccione otro.`,
        color: "warning",
      });
      setForm((p) => ({ ...p, chasis: "", marca: "", modelo: "", colore: "", precio: "" }));
      return;
    }
    setChasisValidado(true);
    // Autorización que quedó en curso o aprobada: se retoma la misma.
    const { aut } = d;
    if (aut?.idtext) {
      setAutMotivo(aut.motivo || "");
      setAutDescripcion(aut.descripcion || "");
      setAutIdtext(aut.idtext);
      setAutStatus(aut.status);
      setAutOpen(true);
      if (aut.status === "Progreso") iniciarPolling(aut.idtext);
    }
  };

  const borrador = useBorradorVenta({
    tipo: "contado",
    datos: {
      form,
      tipodoc,
      foto,
      aut:
        autOpen && autIdtext && (autStatus === "Progreso" || autStatus === "Aprobado")
          ? { idtext: autIdtext, status: autStatus, motivo: autMotivo, descripcion: autDescripcion }
          : null,
    },
    resumen: {
      cliente: `${form.nombres} ${form.apellidos}`.trim() || form.cedula,
      chasis: form.chasis,
    },
    conDatos: [form.cedula, form.nombres, form.apellidos, form.chasis].some((v) =>
      String(v || "").trim()
    ),
    restaurar,
    autoRestaurar: restaurarBorrador,
  });

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Venta al Contado"
        subtitle="Registra la venta de un producto/vehículo pagado en su totalidad (sin financiamiento)"
        cta={
          <Stack direction="row" spacing={1.2}>
            {embedded && (
              <Button startIcon={<ArrowBackIcon />} onClick={onExit} sx={btnOutline(FP.color.navy)}>
                Volver
              </Button>
            )}
            <Button startIcon={<RestartAltIcon />} onClick={limpiar} sx={btnOutline(FP.color.navy)}>
              Nuevo
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <AvisoBorradorVenta
          borrador={borrador.oferta}
          etiqueta="venta al contado"
          onContinuar={borrador.continuar}
          onDescartar={borrador.descartar}
          sx={{ mb: 2.4 }}
        />
        {/* Cliente */}
        <Box sx={sectionBox}>
          <SectionTitle icon={PersonOutlineIcon} color={FP.color.accentBlue}>Cliente</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3} md={2}>
              <Stack alignItems="center" spacing={1.2}>
                <Avatar variant="rounded" src={foto ? `data:image/jpeg;base64,${foto}` : undefined}
                  sx={{ width: 110, height: 110, bgcolor: FP.color.surfaceAlt, color: FP.color.textMuted }}>
                  <PersonOutlineIcon sx={{ fontSize: 46 }} />
                </Avatar>
                <input ref={fotoInputRef} type="file" accept="image/*" hidden onChange={seleccionarFoto} />
                <Button size="small" startIcon={<AddAPhotoOutlinedIcon />} onClick={() => fotoInputRef.current?.click()} sx={btnOutline(FP.color.accentBlue)}>
                  Foto
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={9} md={10}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <DocumentoField value={form.cedula} tipo={tipodoc}
                    onChangeValor={(v) => setForm((p) => ({ ...p, cedula: v }))}
                    onChangeTipo={setTipodoc}
                    onBlur={buscarPorCedula} sx={fpInputSx} />
                  {/* Consulta a la red: corre sola y en paralelo a
                      buscarPorCedula, nunca la bloquea. */}
                  <AlertaRed cedula={form.cedula} tipo={tipodoc} sx={{ mt: 0.75 }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth type="number" label="Edad" value={form.edad} onChange={setF("edad")} sx={fpInputSx} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Apodo" value={form.apodo} onChange={setF("apodo")} sx={fpInputSx} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Nombres" value={form.nombres} onChange={setF("nombres")} sx={fpInputSx} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Apellidos" value={form.apellidos} onChange={setF("apellidos")} sx={fpInputSx} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField select fullWidth label="Sexo" value={form.sexo} onChange={setF("sexo")} sx={fpInputSx}>
                    <MenuItem value="Femenino">Femenino</MenuItem>
                    <MenuItem value="Masculino">Masculino</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField select fullWidth label="Estado civil" value={form.ecivil} onChange={setF("ecivil")} sx={fpInputSx}>
                    <MenuItem value="Casado">Casado</MenuItem>
                    <MenuItem value="Soltero">Soltero</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField select fullWidth label="Dependientes" value={form.depend} onChange={setF("depend")} sx={fpInputSx}>
                    {["0", "1", "2", "3", "4", "5", "6", "7"].map((n) => (
                      <MenuItem key={n} value={n}>{n}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField select fullWidth label="Nacionalidad" value={form.nacionalidad} onChange={setF("nacionalidad")} sx={fpInputSx}>
                    <MenuItem value="Dominicano">Dominicano</MenuItem>
                    <MenuItem value="Extranjero">Extranjero</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>

        {/* Contacto */}
        <Box sx={sectionBox}>
          <SectionTitle icon={PhoneOutlinedIcon} color={FP.color.info}>Contacto</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth label="Dirección" value={form.direccion} onChange={setF("direccion")} sx={fpInputSx} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete options={provincias} value={form.provincia || null}
                onChange={(_, v) => setForm((p) => ({ ...p, provincia: v || "" }))}
                renderInput={(params) => <TextField {...params} label="Provincia" sx={fpInputSx} />} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Teléfono" value={form.telefono} onChange={setF("telefono")} sx={fpInputSx} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Celular" value={form.celular} onChange={setF("celular")} sx={fpInputSx} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Email" value={form.email} onChange={setF("email")} sx={fpInputSx} />
            </Grid>
          </Grid>
        </Box>

        {/* Venta */}
        <Box sx={{ ...sectionBox, mb: 0 }}>
          <SectionTitle icon={DirectionsCarFilledOutlinedIcon} color={FP.color.success}>Producto / Venta</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                freeSolo
                autoHighlight
                selectOnFocus
                handleHomeEndKeys
                filterOptions={filtrarChasis}
                options={chasisOpts}
                loading={loadingChasis}
                value={form.chasis || null}
                getOptionLabel={(o) => (typeof o === "string" ? o : o.chasis || "")}
                isOptionEqualToValue={(o, v) => (o.chasis || o) === (v.chasis || v)}
                renderOption={(props, o) => (
                  <li {...props} key={o.chasis}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>{o.chasis}</Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                        {[o.marca, o.modelo, o.colore].filter(Boolean).join(" · ")}
                      </Typography>
                    </Box>
                  </li>
                )}
                onChange={(_, v) => {
                  const ch = typeof v === "string" ? v : v?.chasis || "";
                  if (ch) validarChasis(ch);
                  else {
                    setChasisValidado(false);
                    setForm((p) => ({ ...p, chasis: "", marca: "", modelo: "", colore: "", precio: "" }));
                  }
                }}
                renderInput={(params) => <TextField {...params} label="Chasis" sx={fpInputSx} />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField fullWidth label="Marca" value={form.marca} InputProps={{ readOnly: true }} sx={fpInputSx} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField fullWidth label="Modelo" value={form.modelo} InputProps={{ readOnly: true }} sx={fpInputSx} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField fullWidth label="Color" value={form.colore} InputProps={{ readOnly: true }} sx={fpInputSx} />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <TextField fullWidth type="number" label="Precio" value={form.precio} onChange={setF("precio")} sx={fpInputSx}
                InputProps={{ startAdornment: <InputAdornment position="start">RD$</InputAdornment> }} />
            </Grid>
            {!combinado && (
              <>
                <Grid item xs={12} sm={4} md={3}>
                  <TextField select fullWidth label="Tipo de pago" value={form.tipopago} onChange={setF("tipopago")} sx={fpInputSx}>
                    {tipospago.map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4} md={6}>
                  <TextField fullWidth label="Descripción / Referencia" value={form.descripcionpago} onChange={setF("descripcionpago")} sx={fpInputSx} />
                  <Button size="small" startIcon={<AddIcon />} onClick={agregarPago} sx={{ ...btnGhost, mt: 0.6 }}>
                    Combinar formas de pago
                  </Button>
                </Grid>
              </>
            )}
            {combinado && (
              <Grid item xs={12}>
                <Box sx={{ p: 2, borderRadius: 2, border: `1px solid ${FP.color.border}`, bgcolor: "#fff" }}>
                  <Typography sx={{ fontSize: "0.74rem", fontWeight: 800, letterSpacing: "0.08em",
                    textTransform: "uppercase", color: FP.color.textSecondary, mb: 1.6 }}>
                    Formas de pago
                  </Typography>
                  <Stack spacing={1.6}>
                    {form.pagos.map((x, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <Grid container spacing={1.5} key={i} alignItems="center">
                        <Grid item xs={12} sm={4}>
                          <TextField select fullWidth label={`Forma de pago ${i + 1}`} value={x.tipopago}
                            onChange={(e) => setPago(i, "tipopago", e.target.value)} sx={fpInputSx}>
                            {tipospago.map((t) => (
                              <MenuItem key={t} value={t}>{t}</MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField fullWidth type="number" label="Monto" value={x.valor}
                            onChange={(e) => setPago(i, "valor", e.target.value)} sx={fpInputSx}
                            InputProps={{ startAdornment: <InputAdornment position="start">RD$</InputAdornment> }} />
                        </Grid>
                        <Grid item xs={10} sm={4}>
                          <TextField fullWidth label="Referencia" value={x.referencia}
                            onChange={(e) => setPago(i, "referencia", e.target.value)} sx={fpInputSx} />
                        </Grid>
                        <Grid item xs={2} sm={1} sx={{ display: "flex", justifyContent: "center" }}>
                          <Tooltip title="Quitar esta forma de pago">
                            <IconButton onClick={() => quitarPago(i)} sx={{ color: FP.color.danger }}>
                              <DeleteOutlineIcon />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    ))}
                  </Stack>
                  <Box sx={{ mt: 1.6, pt: 1.6, borderTop: `1px solid ${FP.color.border}`, display: "flex",
                    alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
                    <Button size="small" startIcon={<AddIcon />} onClick={agregarPago} sx={btnOutline(FP.color.navy)}>
                      Agregar forma de pago
                    </Button>
                    <Typography sx={{ fontSize: "0.86rem", fontWeight: 700,
                      color: Math.abs(faltaAsignar) > 0.009 ? FP.color.danger : FP.color.success }}>
                      {textoAsignado}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}
            {usaVendedor && (
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={usuarios}
                  value={form.vendedor || null}
                  onChange={(_, v) => setForm((p) => ({ ...p, vendedor: v || "" }))}
                  isOptionEqualToValue={(o, v) => o === v}
                  renderInput={(params) => <TextField {...params} label="Vendedor" sx={fpInputSx} />}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={2} label="Comentario" value={form.comentario} onChange={setF("comentario")} inputProps={{ maxLength: 1000 }} />
            </Grid>
          </Grid>

          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2.5 }}>
            <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: FP.color.textPrimary }}>
              Total: RD$ {fmtMoney(form.precio)}
            </Typography>
            <Button
              size="large"
              startIcon={guardando ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              onClick={() => guardar()}
              disabled={guardando || !config}
              sx={{ ...btnSolid(FP.color.success, FP.color.successDark), py: 1.2, px: 4 }}
            >
              Vender
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* Diálogo de autorización (precio < costo) */}
      <Dialog open={autOpen} onClose={() => {}} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.2 }}>
          <GppMaybeOutlinedIcon sx={{ color: FP.color.warning }} />
          Se requiere autorización
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.9rem", color: FP.color.textSecondary, mb: 2 }}>{autMotivo}</Typography>
          {autStatus === "" && (
            <Typography sx={{ fontSize: "0.84rem", color: FP.color.textMuted }}>
              Haz clic en &quot;Solicitar autorización&quot; para enviar la petición al administrador.
            </Typography>
          )}
          {autStatus === "Progreso" && (
            <Stack direction="row" spacing={1.4} alignItems="center">
              <CircularProgress size={18} />
              <Typography sx={{ fontSize: "0.9rem", color: FP.color.danger, fontWeight: 700 }}>Esperando aprobación…</Typography>
            </Stack>
          )}
          {autStatus === "Aprobado" && (
            <Stack direction="row" spacing={1.4} alignItems="center">
              <CheckCircleOutlineIcon sx={{ color: FP.color.success }} />
              <Typography sx={{ fontSize: "0.9rem", color: FP.color.success, fontWeight: 700 }}>
                Autorización aprobada. Ya puedes registrar la venta.
              </Typography>
            </Stack>
          )}
          {autStatus === "Cancelado" && (
            <Typography sx={{ fontSize: "0.9rem", color: FP.color.danger, fontWeight: 700 }}>
              Autorización declinada{autComentario ? ` — mensaje: ${autComentario}` : ""}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={cancelarAutorizacion} sx={btnGhost}>Cerrar</Button>
          {autStatus === "" && (
            <Button disableElevation onClick={solicitarAutorizacion} sx={btnSolid(FP.color.warning, "#B45309")}>
              Solicitar autorización
            </Button>
          )}
          {autStatus === "Aprobado" && (
            <Button disableElevation startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <PaidOutlinedIcon />}
              disabled={guardando} onClick={() => guardar(autIdtext)} sx={btnSolid(FP.color.success, FP.color.successDark)}>
              Registrar venta
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
